import React, { useEffect, useRef } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NEXT_SECTION_SELECTOR = '#intro, #intro_villa, #next-section, #quote-form';
const HERO_BG = '../../../assets/images/photos/HERO.webp';

const HeroSection = () => {
  const heroRef = useRef(null);

  const phoneNumber = '+421 905 123 456';
  const whatsappNumber = '+421905123456';

  const handlePhoneClick = () => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleWhatsAppClick = () => {
    window.open(
      `https://wa.me/${whatsappNumber}?text=Dobrý deň, mám záujem o zasklenie balkóna. Prosím o cenový odhad.`,
      '_blank'
    );
  };

  const scrollToNext = () => {
    const el =
      document.querySelector(NEXT_SECTION_SELECTOR) ||
      document.getElementById('quote-form');
    if (!el) return;
    const headerOffset = 80;
    const rect = el.getBoundingClientRect();
    const offset = rect.top + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offset, behavior: 'smooth' });
  };

  // твой существующий CSS-инжект и обработчик скролла — без изменений
  useEffect(() => {
    const STYLE_ID = 'hero-grad-css';
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.innerHTML = `
      @property --grad1 { syntax: '<color>'; inherits: false; initial-value: #FFE7E7; }
      @property --grad2 { syntax: '<color>'; inherits: false; initial-value: #FFE7E7; }
      body::before{ content:""; position: fixed; inset: 0; z-index: -1;
        background: linear-gradient(180deg, var(--grad1), var(--grad2)); pointer-events: none; }
      body{ --grad1:#FFE7E7; --grad2:#FFE7E7; transition: --grad1 400ms ease, --grad2 400ms ease; }
      body.is-below-hero{ --grad1:#061a2b; --grad2:#0e3a5a; }
      .bg-black-bg{ background-color:#101010; }
      [data-name="villa_bg"]{ background-color: var(--hero-bg, transparent); transition: background-color 600ms cubic-bezier(.4,0,.2,1); }
      .hero-coloring [data-hero] *{ pointer-events:none !important; }
    `;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    let lastFlag = false;
    let coloringTimer = null;

    const onScroll = () => {
      const bottom =
        heroRef.current?.getBoundingClientRect().bottom ?? window.innerHeight;
      const nowBelow = bottom <= 0;
      if (nowBelow !== lastFlag) {
        lastFlag = nowBelow;
        document.body.classList.toggle('is-below-hero', nowBelow);
        document.body.classList.add('hero-coloring');
        if (coloringTimer) window.clearTimeout(coloringTimer);
        coloringTimer = window.setTimeout(() => {
          document.body.classList.remove('hero-coloring');
        }, 300);
      }
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      document.body.classList.remove('hero-coloring', 'is-below-hero');
    };
  }, []);

  return (
    <section
      id="hero"
      ref={heroRef}
      data-hero
      className="relative min-h-[100svh] lg:min-h-screen overflow-hidden"
    >
      {/* === BACKGROUND PHOTO LAYER === */}
      <div className="absolute inset-0">
        <Image
          src={HERO_BG}
          alt=""
          className="w-full h-full object-cover"
          priority
        />
        <div className="absolute inset-0 bg-black/40" aria-hidden />
      </div>

      {/* === CONTENT === */}
      <div className="relative z-10 bg-black/0 w-full h-full" data-name="villa_bg">
        <div className="container mx-auto px-4 relative">
          <div className="grid lg:grid-cols-2 items-center gap-8 md:gap-10 lg:gap-12 min-h-[100svh] lg:min-h-screen py-14 md:py-16 lg:py-20">
            {/* Left: Text / CTA */}
            <div className="space-y-6 md:space-y-8">
              <div className="inline-flex items-center space-x-3 bg-secondary/20 backdrop-blur-sm rounded-full px-5 py-2.5 md:px-6 md:py-3 border border-secondary/30">
                <Icon name="Award" size={20} color="var(--color-accent)" />
                <span className="text-white font-semibold">24 rokov skúseností</span>
              </div>

              <div className="space-y-3 md:space-y-4">
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight">
                  Zasklenie balkónov
                  <span className="block text-accent">a terás</span>
                </h1>
                <p className="text-lg md:text-2xl text-white/90 font-body leading-relaxed">
                  Ochrana pred počasím, zníženie hluku a rozšírenie obytného priestoru s prémiovými hliníkovými systémami
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                {[
                  { icon: 'Shield', text: 'Ochrana pred počasím' },
                  { icon: 'Volume2', text: 'Zníženie hluku až o 80%' },
                  { icon: 'TrendingUp', text: 'Zvýšenie hodnoty nehnuteľnosti' },
                ].map((p, i) => (
                  <div
                    key={i}
                    className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-3.5 sm:p-4"
                  >
                    <Icon name={p.icon} size={24} color="var(--color-accent)" />
                    <span className="text-white font-medium text-sm">{p.text}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button
                  variant="default"
                  size="lg"
                  onClick={scrollToNext}
                  iconName="Calculator"
                  iconPosition="left"
                  className="bg-accent hover:bg-accent/90 text-white font-cta text-lg px-8 py-4 shadow-cta"
                >
                  Získať cenový odhad
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handlePhoneClick}
                  iconName="Phone"
                  iconPosition="left"
                  className="border-white text-white hover:bg-white hover:text-primary font-cta text-lg px-8 py-4"
                >
                  {phoneNumber}
                </Button>
              </div>

              <div className="flex items-center space-x-6 pt-2 sm:pt-4">
                <button
                  onClick={handleWhatsAppClick}
                  className="flex items-center space-x-2 text-white/80 hover:text-white transition-smooth"
                >
                  <Icon name="MessageCircle" size={20} />
                  <span className="font-body text-white">WhatsApp</span>
                </button>
                <div className="flex items-center space-x-2 text-white/80">
                  <Icon name="Clock" size={20} />
                  <span className="font-body text-white">Odpoveď do 2 hodín</span>
                </div>
              </div>
            </div>

            {/* Right column свободна — без изменений */}
          </div>
        </div>
      </div>

      {/* Scroll indicator — скрыт на мобильных/планшетах */}
      <button
        type="button"
        onClick={scrollToNext}
        aria-label="Scroll to next section"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 hidden lg:block"
      >
        <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse" />
        </div>
      </button>
    </section>
  );
};

export default HeroSection;